// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect } from 'react';

import type { StrongOmit, RenderableUser, UserId } from '@evolvemep/api-ts';
import { Badge, type BadgeProps, Flex, Stack, Text, Loader } from '@mantine/core';

import { useBadgeTruncation, BADGE_GAP, OVERFLOW_BADGE_WIDTH } from 'components/Mantine/Labels/useBadgeTruncation';
import { UserAvatar } from 'components/Mantine/UserInfo';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { useUsersInfo } from 'hooks/user/useUsersInfo';

import { AgGridStyleTooltip } from './AgGridStyleTooltip';

type Props = {
  hideTooltip?: boolean;
} & RequireAtLeastOne<{
  user?: StrongOmit<RenderableUser, 'userId'>;
  userId?: UserId;
}>;

const StyledBadge = ({
  name,
  children,
  ...props
}: BadgeProps &
  RequireAtLeastOne<{
    name?: string;
    children?: ReactNode;
  }>) => (
  <Badge
    tt="none"
    variant="filled"
    pl={name ? 3 : undefined}
    c="black"
    color="gray.1"
    fw={500}
    leftSection={name ? <UserAvatar name={name} size="xs" /> : undefined}
    {...props}
    style={{
      pointerEvents: 'none',
      // TODO: `.style` can now be a function, need to handle it
      // oxlint-disable-next-line typescript/no-misused-spread
      ...props.style,
    }}
  >
    {name ? name.split('@')[0] : children}
  </Badge>
);

export const UserInfoBadge = ({ user: userFromParent, userId, hideTooltip }: Props) => {
  const { fetchUserInfo, userInfo } = useUsersInfo();

  useEffect(() => {
    if (userId) {
      void fetchUserInfo(userId);
    }
  }, [fetchUserInfo, userId]);

  const user = userFromParent
    ? {
        completeName: validateUserNameOrEmail(userFromParent),
        email: userFromParent.userEmail,
      }
    : userId
      ? userInfo[userId]
      : null;

  if (!user) return <Loader size="xs" type="dots" />;

  return (
    <AgGridStyleTooltip label={user.completeName} openDelay={250} hidden={hideTooltip}>
      <StyledBadge miw={70} name={user.completeName} />
    </AgGridStyleTooltip>
  );
};

/** For use in AG Grids, to truncate the list and show a tooltip with all users. */
export const UserInfoList = ({
  users,
  tooltipLabel = 'Assignees',
  truncate = true,
  fallback = '--',
}: {
  users: RenderableUser[];
  tooltipLabel?: string;
  truncate?: boolean;
  fallback?: ReactNode;
}) => {
  const { containerRef, visibleCount, remainder } = useBadgeTruncation(users.length, '[data-user-badge]', truncate);

  if (users.length === 0) return fallback;

  return (
    <AgGridStyleTooltip
      openDelay={250}
      hidden={!truncate}
      label={
        <Stack gap={0} ta="left">
          {!!tooltipLabel && <Text fw={500}>Assignees</Text>}
          {users.map((u) => (
            <Text key={u.userId}>{validateUserNameOrEmail(u)}</Text>
          ))}
        </Stack>
      }
    >
      <Flex gap={BADGE_GAP} ref={containerRef} w="100%" miw={0} style={{ overflow: 'hidden' }}>
        {users.map((u, i) => (
          <StyledBadge
            key={u.userId}
            miw={70}
            data-user-badge
            style={{
              flexShrink: 0,
              visibility: i < visibleCount ? 'visible' : 'hidden',
              position: i < visibleCount ? undefined : 'absolute',
            }}
            name={validateUserNameOrEmail(u)}
          />
        ))}
        {remainder > 0 && (
          <StyledBadge miw={OVERFLOW_BADGE_WIDTH} style={{ flexShrink: 0 }}>
            +{Math.min(remainder, 99)}
          </StyledBadge>
        )}
      </Flex>
    </AgGridStyleTooltip>
  );
};
